import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import { Badge } from "@vilocnv/allsetra-core";
import { Clock, MileageIcon } from "assets/icons";
import { HiddenBadgeIcon } from "components/ui/icons";
import { IObjectRideSegment } from "app/data/types";
import {
  getSegmentColor,
  getSegmentDistanceKm,
  getSegmentDurationMinutes,
  MixedRideTimelineStop,
  MIXED_RIDE_SEGMENT_COLORS,
  normalizeSegmentMode,
  isUtcMidnight
} from "app/data/helpers/mixedRideSegmentHelpers";
import { ObjectRideModeEnum } from "app/data/helpers/objectRideHelpers";
import { useTranslation } from "react-i18next";

const TimelineRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  marginTop: 8
});

const StopRow = styled(Box)({
  display: "flex",
  alignItems: "stretch"
});

const TimeColumn = styled(Typography)({
  flex: "0 0 52px",
  paddingTop: 4,
  fontSize: 13,
  fontWeight: 500,
  color: "#76828F"
});

const MarkerColumn = styled(Box)({
  flex: "0 0 28px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative"
});

const Connector = styled(Box, {
  shouldForwardProp: (prop) => prop !== "connectorColor"
})<{ connectorColor: string }>(({ connectorColor }) => ({
  position: "absolute",
  left: "50%",
  top: 0,
  bottom: 0,
  width: 3,
  transform: "translateX(-50%)",
  backgroundColor: connectorColor,
  borderRadius: 2,
  zIndex: 0
}));

const ContentColumn = styled(Box)({
  flex: 1,
  minWidth: 0,
  paddingLeft: 12,
  paddingBottom: 20
});

const AddressTitle = styled("h3")({
  margin: 0,
  fontFamily: "Gilroy",
  fontSize: 16,
  fontWeight: 500,
  lineHeight: "120%",
  color: "#1D1F2B",
  wordBreak: "break-word"
});

const SegmentCard = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "selected" &&
    prop !== "modeColor" &&
    prop !== "isPrivate" &&
    prop !== "disabled"
})<{
  selected?: boolean;
  modeColor: string;
  isPrivate?: boolean;
  disabled?: boolean;
}>(({ selected, modeColor, isPrivate, disabled }) => ({
  marginTop: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "10px 12px",
  borderRadius: 10,
  backgroundColor: isPrivate ? "#F4F5F7" : "#E7F8F6",
  border: selected ? `1px solid ${modeColor}` : "1px solid transparent",
  cursor: disabled ? "default" : "pointer"
}));

const StartMarker = () => (
  <Box
    width={24}
    height={24}
    minHeight={24}
    borderRadius="50%"
    bgcolor={MIXED_RIDE_SEGMENT_COLORS.business}
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    zIndex={1}
  >
    <Box
      width={0}
      height={0}
      ml="2px"
      borderTop="5px solid transparent"
      borderBottom="5px solid transparent"
      borderLeft="8px solid #FFFFFF"
    />
  </Box>
);

const IntermediateMarker = ({
  fromColor,
  toColor
}: {
  fromColor: string;
  toColor: string;
}) => {
  const isSplit = fromColor !== toColor;

  return (
    <Box
      width={14}
      height={14}
      minHeight={14}
      borderRadius="50%"
      flexShrink={0}
      zIndex={1}
      boxSizing="border-box"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      position="relative"
      border={isSplit ? "none" : `2.5px solid ${toColor}`}
      bgcolor="#FFFFFF"
    >
      {isSplit ? (
        <>
          <Box
            position="absolute"
            left={0}
            top={0}
            width="50%"
            height="100%"
            bgcolor={fromColor}
          />
          <Box
            position="absolute"
            right={0}
            top={0}
            width="50%"
            height="100%"
            bgcolor={toColor}
          />
          <Box
            width={7}
            height={7}
            borderRadius="50%"
            bgcolor="#FFFFFF"
            zIndex={1}
          />
        </>
      ) : null}
    </Box>
  );
};

const EndMarker = () => (
  <Box
    width={22}
    height={22}
    minHeight={22}
    borderRadius="50%"
    bgcolor="#1D1F2B"
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    zIndex={1}
  >
    <Box width={8} height={8} borderRadius="1px" bgcolor="#F9FBFF" />
  </Box>
);

interface Props {
  stops: MixedRideTimelineStop[];
  selectedSegmentId?: string | null;
  onSegmentSelect?: (segmentId: string) => void;
}

const splitAddress = (index: number, resolvedAddress: string) => {
  if (!resolvedAddress) return index === 0 ? "N/A" : "";
  const parts = resolvedAddress.split(",");
  return parts[index] !== undefined
    ? `${parts[index]}${index !== 2 ? ", " : ""}`
    : index === 0
      ? "N/A"
      : "";
};

const MixedRideTimeline = ({
  stops,
  selectedSegmentId,
  onSegmentSelect
}: Props) => {
  const { t } = useTranslation();

  const renderAddress = (address: string) => {
    const addressIsHidden = t("typography.addressIsHidden");
    const addressUnknown = t("typography.addressUnknown");

    if (address === addressIsHidden) {
      return (
        <Badge
          colorScheme="blank"
          icon={<HiddenBadgeIcon />}
          textWeight="light"
        >
          {addressIsHidden}
        </Badge>
      );
    }

    if (address === addressUnknown) {
      return (
        <Badge colorScheme="blank" textWeight="light">
          {addressUnknown}
        </Badge>
      );
    }

    if (!address || address === "N/A") {
      return (
        <Badge colorScheme="blank" textWeight="light">
          {t("typography.na")}
        </Badge>
      );
    }

    return (
      <>
        {splitAddress(0, address)}
        <br />
        {splitAddress(1, address) + splitAddress(2, address)}
      </>
    );
  };

  const renderSegmentCard = (segment: IObjectRideSegment) => {
    const mode = normalizeSegmentMode(segment.mode);
    const isPrivate = mode === ObjectRideModeEnum.PRIVATE;
    const color = getSegmentColor(segment.mode);
    const selected = selectedSegmentId === segment.uniqueId;
    const isHidden = Boolean(segment.isPrivacyHidden);
    const distanceKm = getSegmentDistanceKm(segment);

    return (
      <SegmentCard
        role={isHidden ? undefined : "button"}
        tabIndex={isHidden ? -1 : 0}
        modeColor={color}
        isPrivate={isPrivate}
        selected={selected}
        disabled={isHidden}
        data-testid={`mixed-ride-segment-${segment.uniqueId}`}
        onClick={() => {
          if (!isHidden) onSegmentSelect?.(segment.uniqueId);
        }}
        onKeyDown={(event) => {
          if (isHidden) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSegmentSelect?.(segment.uniqueId);
          }
        }}
      >
        <Box color={color} fontSize={13} fontWeight={600}>
          {isPrivate ? t("typography.private") : t("typography.business")}
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {!isHidden ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <MileageIcon />
              <Typography fontSize={12} color="#323946">
                {distanceKm} km
              </Typography>
            </Stack>
          ) : null}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Clock />
            <Typography fontSize={12} color="#323946">
              {getSegmentDurationMinutes(segment)} min
            </Typography>
          </Stack>
        </Stack>
      </SegmentCard>
    );
  };

  return (
    <TimelineRoot data-testid="mixed-ride-timeline">
      {stops.map((stop, index) => {
        const isFirst = index === 0;
        const isLast = index === stops.length - 1;
        const connectorColor = stop.followingSegment
          ? getSegmentColor(stop.followingSegment.mode)
          : "#D5D9E8";
        const previousColor = stops[index - 1]?.followingSegment
          ? getSegmentColor(stops[index - 1].followingSegment?.mode)
          : connectorColor;

        const isPrivateStart =
          isFirst &&
          normalizeSegmentMode(stop.followingSegment?.mode) ===
            ObjectRideModeEnum.PRIVATE;

        return (
          <StopRow key={`${stop.date}-${index}`}>
            <TimeColumn>
              {stop.date
                ? isUtcMidnight(stop.date)
                  ? moment.utc(stop.date).format("DD MMM")
                  : moment(stop.date).format("HH:mm")
                : ""}
            </TimeColumn>
            <MarkerColumn>
              {isFirst && !isPrivateStart ? (
                <StartMarker />
              ) : isLast ? (
                <EndMarker />
              ) : (
                <IntermediateMarker
                  fromColor={previousColor}
                  toColor={connectorColor}
                />
              )}
              {!isLast && (
                <Connector
                  connectorColor={connectorColor}
                  data-testid="mixed-ride-connector"
                />
              )}
            </MarkerColumn>
            <ContentColumn sx={{ paddingBottom: isLast ? 0 : 2.5 }}>
              <AddressTitle>{renderAddress(stop.address)}</AddressTitle>
              {stop.followingSegment
                ? renderSegmentCard(stop.followingSegment)
                : null}
            </ContentColumn>
          </StopRow>
        );
      })}
    </TimelineRoot>
  );
};

export default MixedRideTimeline;
